import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { FaTimes } from "react-icons/fa";
import { useOrganization } from "../../context/OrganizationContext";
import { useAuth } from "../../context/AuthContext";
import { useCreateTeamActions } from "./hooks/useCreateTeamActions";
import { useTeamCreationData } from "./hooks/useTeamCreationData";
import { useBowlerManagement } from "./hooks/useBowlerManagement";
import { useTeamFormState } from "./hooks/useTeamFormState";
import { useTeamFormValidation } from "./hooks/useTeamFormValidation";
import { AlertBox, IconButton, FormFieldsSkeleton } from "../UI/DesignStandards";
import { notify } from "../UI/DesignStandards/Notifications/notify";
import { sanitizeErrorMessage } from "../../utility/errorSanitization";
import { ContextHeader } from "./components/ContextHeader";
import { TeamDetailsSection } from "./components/TeamDetailsSection";
import { LaneAssignmentFields } from "./components/LaneAssignmentFields";
import { BowlerCard } from "./components/BowlerCard";
import { ActionBar } from "./components/ActionBar";
import { needsOrgBowlerPick } from "../../utility/bowlerMatch";
import {
  assignsStartingLane,
  buildPairs,
  formatPair,
  nextOpenSlot,
  pairStartFromLane,
  physicalLane,
  placementsFromTeams,
  settingsFromSquadRow,
  slotCountForSquad,
  teamsOnPair,
} from "../../utility/laneAssignment";
import {
  DEFAULT_DOUBLES_SINGLES_SETUP,
  bowlersForSinglesFollowUp,
  createTeamBowlerFromSinglesFollowUp,
  createTeamOptionalSinglesNote,
  createTeamSinglesFollowUpNote,
  printDoublesSinglesMode,
  showCreateTeamSinglesToggle,
} from "../../utility/doublesSinglesSetup";
import { DOUBLES_SINGLES } from "../../utility/signupSheets/signupSheetModel";
import { fetchDoublesSinglesSetup } from "../../utility/doublesSinglesSetupApi";
import { findSinglesPrimaryEvent } from "../../utility/signupReview";
import {
  eventsOnSquad,
  resolveSquadEvent,
} from "../../utility/signupAvailability";
import { fetchTournamentSignupSettings } from "../../utility/signupSettings";
import { fetchSquadWalkUpCap } from "../../utility/signupOccupancy";
import { directorPromoteNextWaitlist } from "../../utility/signupRpc";
import {
  assignTeamStartingPair,
  fetchTournamentLaneSettings,
  isLaneSlotTakenError,
} from "../../utility/laneAssignmentApi";
import { listBracketProducts, subscribeBracketProducts } from "../../utility/brackets/api";
import { productsEnabledForSquad } from "../../utility/brackets/entryTickets";

const SinglePageTeamCreation = ({
  squadID,
  tournamentID,
  setTeams,
  onClose,
  squads = [],
  teams = [],
  paperSignup = null,
  paperIndex = 0,
  paperCount = 0,
  onPaperTeamSaved = null,
  onPaperTeamSkip = null,
  onPaperTeamDelete = null,
}) => {
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();

  // Ref for first bowler's name input
  const firstBowlerNameRef = useRef(null);

  // Custom hooks for different concerns
  // Initialize squad state first
  const [selectedSquadId, setSelectedSquadId] = useState(
    paperSignup?.squadId || null
  );
  const [capBlock, setCapBlock] = useState(null);
  
  // Load events based on selected squad
  const {
    events,
    sidePotEvents,
    allEventConfigs,
    tournamentBowlers,
    eventsBySquad,
    extrasReady,
    existingBowlers,
    loading,
    error: dataError,
  } = useTeamCreationData(tournamentID, currentOrganization, selectedSquadId);

  const eventsForForm = useMemo(() => {
    if (!paperSignup?.eventId) return events;
    if ((events || []).some((row) => row.id === paperSignup.eventId)) {
      return events;
    }
    return [
      ...(events || []),
      {
        id: paperSignup.eventId,
        eventName: paperSignup.eventName,
        bowlerQuantity: paperSignup.teamSize || 1,
        isPrimary: true,
      },
    ];
  }, [
    events,
    paperSignup?.eventId,
    paperSignup?.eventName,
    paperSignup?.teamSize,
  ]);

  // Then, get form state with loaded events
  const {
    selectedSquad,
    selectedEvent,
    teamName,
    startingPair,
    errors,
    isSubmitting,
    showCancelConfirm,
    hasChanges,
    handleSquadChange: handleSquadChangeInternal,
    handleEventChange,
    handleTeamNameChange,
    handleTeamNameBlur,
    handleCancel,
    handleConfirmCancel,
    handleCancelCancel,
    resetForm,
    setHasChanges,
    setIsSubmitting,
    setErrors,
    setStartingPair,
    setSelectedSquad,
    setSelectedEvent,
    setTeamName,
  } = useTeamFormState(squads, eventsForForm);

  // Wrap handleSquadChange to update both the form state and the squad ID for event loading
  const handleSquadChange = useCallback(
    (squadId) => {
      setSelectedSquadId(squadId);
      handleSquadChangeInternal(squadId);
    },
    [handleSquadChangeInternal]
  );

  const squadEvents = useMemo(() => {
    if (paperSignup) return eventsForForm;
    if (!selectedSquad) return [];
    return eventsOnSquad(selectedSquad, eventsForForm, eventsBySquad);
  }, [paperSignup, selectedSquad, eventsForForm, eventsBySquad]);

  useEffect(() => {
    if (paperSignup || !extrasReady) return;
    setSelectedEvent((current) =>
      resolveSquadEvent(selectedSquad, eventsForForm, eventsBySquad, current)
    );
  }, [
    paperSignup,
    extrasReady,
    selectedSquad,
    eventsForForm,
    eventsBySquad,
    setSelectedEvent,
  ]);

  const {
    bowlers,
    setBowlers,
    initializeBowlers,
    handleBowlerUpdate,
    handleBowlerBlur,
    handleBowlerSidePotToggle,
    handleBowlerTicketChange,
    handleBowlerSinglesToggle,
    handleLookupToggle,
    handleLookupSearch,
    handleLookupSelect,
    handleCatalogSelect,
    handleCatalogClear,
    catalogAssignment,
    catalogReady,
    applySignupReviewBowlers,
    skipPaperRefillRef,
    resetBowlers,
    getAllEventStatus,
  } = useBowlerManagement(
    selectedEvent,
    existingBowlers,
    tournamentBowlers,
    allEventConfigs,
    tournamentID
  );

  const { validateForm, canSave } = useTeamFormValidation();
  const canEnterBowlers = Boolean(selectedSquad?.id && selectedEvent?.id);
  const [doublesSinglesSetup, setDoublesSinglesSetup] = useState(
    DEFAULT_DOUBLES_SINGLES_SETUP
  );
  const singlesEvent = findSinglesPrimaryEvent(eventsForForm, {
    excludeEventId: selectedEvent?.id,
    squadId: selectedSquad?.id,
  });
  const showSinglesToggle = showCreateTeamSinglesToggle({
    event: selectedEvent,
    setup: doublesSinglesSetup,
    singlesEvent,
  });
  const [singlesFollowUp, setSinglesFollowUpState] = useState(null);
  const singlesFollowUpRef = useRef(null);
  const setSinglesFollowUp = useCallback((next) => {
    singlesFollowUpRef.current = next;
    setSinglesFollowUpState(next);
  }, []);
  const singlesFollowUpActive = Boolean(singlesFollowUp?.remaining?.length);
  const [bracketProducts, setBracketProducts] = useState([]);

  useEffect(() => {
    if (!tournamentID) {
      setBracketProducts([]);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const products = await listBracketProducts(tournamentID);
        if (!cancelled) setBracketProducts(products || []);
      } catch {
        if (!cancelled) setBracketProducts([]);
      }
    };
    load();
    return subscribeBracketProducts(tournamentID, () => {
      load();
    });
  }, [tournamentID]);

  const squadBracketProducts = useMemo(
    () => productsEnabledForSquad(bracketProducts, selectedSquad?.id),
    [bracketProducts, selectedSquad?.id]
  );

  useEffect(() => {
    let cancelled = false;
    fetchDoublesSinglesSetup(tournamentID)
      .then((next) => {
        if (!cancelled) setDoublesSinglesSetup(next);
      })
      .catch(() => {
        if (!cancelled) setDoublesSinglesSetup(DEFAULT_DOUBLES_SINGLES_SETUP);
      });
    return () => {
      cancelled = true;
    };
  }, [tournamentID]);

  const persistLaneAssignment = useCallback(
    async (createdTeam) => {
      if (!createdTeam?.id || !selectedSquad?.id) return;
      const pairStart = pairStartFromLane(startingPair);
      if (!pairStart) return;
      const range = settingsFromSquadRow(selectedSquad);
      const openPairs = buildPairs(
        range.startLane,
        range.endLane,
        range.blackouts
      ).filter((pair) => !pair.blocked);
      const squadTeams = (teams || []).filter(
        (team) => team.squadID === selectedSquad.id
      );
      const placements = placementsFromTeams(squadTeams);
      const squadTeamCount = squadTeams.length + 1;
      const cap = openPairs.length
        ? slotCountForSquad(squadTeamCount, openPairs.length)
        : teamsOnPair(placements, pairStart).length + 1;
      const slotValue = nextOpenSlot(placements, pairStart, cap);
      if (!slotValue) {
        notify.error(
          `Pair ${formatPair(pairStart)} is full. The team was created without a lane.`
        );
        return;
      }
      const laneValue = physicalLane(startingPair);
      try {
        const settings = await fetchTournamentLaneSettings(tournamentID);
        const startingLane = assignsStartingLane(
          settings,
          selectedEvent?.bowlerQuantity
        )
          ? laneValue
          : null;
        await assignTeamStartingPair({
          teamId: createdTeam.id,
          startingPair: pairStart,
          startingLane,
          slot: slotValue,
        });
      } catch (error) {
        if (isLaneSlotTakenError(error)) {
          notify.error(
            `Pair ${formatPair(pairStart)} is full. The team was created without a lane.`
          );
          return;
        }
        notify.error(
          `The team was created, but the starting lane could not be saved.`
        );
      }
    },
    [selectedEvent, selectedSquad, startingPair, teams, tournamentID]
  );

  const setTeamsWithLane = useCallback(
    async (createdTeam, shouldClose) => {
      await persistLaneAssignment(createdTeam);
      setTeams(createdTeam, shouldClose);
    },
    [persistLaneAssignment, setTeams]
  );

  // Refocus callback for after team creation
  const handleTeamCreated = () => {
    // Focus the first bowler's name input after a short delay
    setTimeout(() => {
      if (firstBowlerNameRef.current) {
        firstBowlerNameRef.current.focus();
      }
    }, 100);
  };

  const { handleSubmit } = useCreateTeamActions(
    setTeams,
    null, // setCurrentStep not used in single page
    setErrors,
    setIsSubmitting,
    resetForm,
    null, // validateStep not used in single page
    handleTeamCreated
  );

  // Bowler slots reset when the event changes. Do not show them until both
  // squad and event are selected. Paper review fills the slots from the sheet.
  // After a Doubles save, fill one Singles bowler at a time (no side pots copied).
  useEffect(() => {
    const followUp = singlesFollowUpRef.current;
    if (followUp?.remaining?.length) {
      if (selectedEvent?.id !== followUp.singlesEventId) return;
      setBowlers([
        createTeamBowlerFromSinglesFollowUp(followUp.remaining[0], 0),
      ]);
      setTeamName("");
      setStartingPair("");
      return;
    }
    if (paperSignup) return;
    if (canEnterBowlers && selectedEvent?.bowlerQuantity) {
      initializeBowlers(selectedEvent.bowlerQuantity, {
        enterSingles:
          Number(selectedEvent.bowlerQuantity) === 2 &&
          printDoublesSinglesMode(doublesSinglesSetup) ===
            DOUBLES_SINGLES.always,
      });
    } else {
      resetBowlers();
    }
  }, [
    paperSignup,
    canEnterBowlers,
    selectedEvent?.id,
    selectedEvent?.bowlerQuantity,
    initializeBowlers,
    resetBowlers,
    singlesFollowUp,
    setBowlers,
    setTeamName,
    setStartingPair,
    doublesSinglesSetup,
  ]);

  const appliedSignupKeyRef = useRef("");
  useEffect(() => {
    appliedSignupKeyRef.current = "";
    skipPaperRefillRef.current = false;
  }, [paperSignup?.key, skipPaperRefillRef]);

  useEffect(() => {
    if (!paperSignup?.squadId || !squads.length) return;
    const squad = squads.find((row) => row.id === paperSignup.squadId);
    if (!squad) return;
    setSelectedSquadId(squad.id);
    setSelectedSquad(squad);
  }, [paperSignup?.squadId, squads, setSelectedSquad]);

  useEffect(() => {
    if (!paperSignup?.eventId) return;
    if (singlesFollowUpRef.current?.remaining?.length) return;
    const fromList = (eventsForForm || []).find(
      (row) => row.id === paperSignup.eventId
    );
    setSelectedEvent(
      fromList || {
        id: paperSignup.eventId,
        eventName: paperSignup.eventName,
        bowlerQuantity: paperSignup.teamSize || 1,
        isPrimary: true,
      }
    );
  }, [
    paperSignup?.eventId,
    paperSignup?.eventName,
    paperSignup?.teamSize,
    eventsForForm,
    setSelectedEvent,
    singlesFollowUp,
  ]);

  useEffect(() => {
    if (!paperSignup || loading || !catalogReady || !canEnterBowlers) return;
    if (singlesFollowUpRef.current?.remaining?.length) return;
    if (paperSignup.eventId && selectedEvent?.id !== paperSignup.eventId) {
      return;
    }
    const ticksKey = (paperSignup.rows || [])
      .map((row) => (row?.ticks || []).join("|"))
      .join(",");
    const applyKey = `${paperSignup.key}:${existingBowlers.length}:${
      tournamentBowlers.length
    }:${catalogAssignment?.catalogID || ""}:${
      (sidePotEvents || []).map((event) => event.id).join(",")
    }:${ticksKey}`;
    if (appliedSignupKeyRef.current === applyKey) return;
    if (
      skipPaperRefillRef.current &&
      appliedSignupKeyRef.current.startsWith(`${paperSignup.key}:`)
    ) {
      return;
    }
    appliedSignupKeyRef.current = applyKey;
    applySignupReviewBowlers(paperSignup, {
      teamSize: paperSignup.teamSize || selectedEvent?.bowlerQuantity,
      existingBowlers,
      tournamentBowlers,
      sidePotEvents,
      alwaysEnterSingles:
        printDoublesSinglesMode(doublesSinglesSetup) === DOUBLES_SINGLES.always,
    });
    setTeamName(paperSignup.teamName || "");
    setStartingPair(paperSignup.lane || "");
  }, [
    paperSignup,
    loading,
    catalogReady,
    catalogAssignment,
    canEnterBowlers,
    selectedEvent,
    existingBowlers,
    tournamentBowlers,
    sidePotEvents,
    applySignupReviewBowlers,
    setTeamName,
    setStartingPair,
    doublesSinglesSetup,
  ]);

  // Track changes from bowler updates
  useEffect(() => {
    const hasBowlerChanges = bowlers.some(
      (bowler) =>
        bowler.name.trim() !== "" ||
        bowler.average !== "" ||
        bowler.email.trim() !== "" ||
        bowler.phone.trim() !== "" ||
        bowler.usbcID.trim() !== ""
    );
    const hasFormChanges = teamName.trim() !== "" || hasBowlerChanges;
    setHasChanges(hasFormChanges);
  }, [bowlers, teamName, setHasChanges]);

  const planAfterTeamSaved = useCallback(
    ({ createAnother }) => {
      const followUp = singlesFollowUpRef.current;
      if (followUp?.remaining?.length) {
        const rest = followUp.remaining.slice(1);
        if (rest.length) {
          return {
            shouldClose: false,
            successMessage:
              "Singles team saved. Set side pots for the next Singles team, then Save Team.",
            apply: () => {
              resetForm();
              setSinglesFollowUp({ ...followUp, remaining: rest });
            },
          };
        }
        const doublesEvent = (eventsForForm || []).find(
          (event) => event.id === followUp.doublesEventId
        );
        if (paperSignup && onPaperTeamSaved) {
          return {
            shouldClose: false,
            successMessage: null,
            apply: () => {
              resetForm();
              setSinglesFollowUp(null);
              appliedSignupKeyRef.current = "";
              onPaperTeamSaved();
            },
          };
        }
        if (followUp.createAnother && doublesEvent) {
          return {
            shouldClose: false,
            successMessage: "Singles team saved. Create the next Doubles team.",
            apply: () => {
              resetForm();
              setSinglesFollowUp(null);
              setSelectedEvent(doublesEvent);
            },
          };
        }
        return {
          shouldClose: true,
          successMessage: null,
          apply: () => {
            resetForm();
            setSinglesFollowUp(null);
            resetBowlers();
            onClose();
          },
        };
      }

      const queue = !singlesEvent
        ? []
        : bowlersForSinglesFollowUp({
            event: selectedEvent,
            setup: doublesSinglesSetup,
            bowlers,
            singlesEvent,
          });
      if (queue.length) {
        return {
          shouldClose: false,
          successMessage:
            "Doubles team saved. Set side pots for Singles, then Save Team.",
          apply: () => {
            resetForm();
            setSinglesFollowUp({
              remaining: queue,
              singlesEventId: singlesEvent.id,
              doublesEventId: selectedEvent.id,
              createAnother,
            });
            skipPaperRefillRef.current = true;
            setSelectedEvent(singlesEvent);
          },
        };
      }

      if (paperSignup && onPaperTeamSaved) {
        return {
          shouldClose: false,
          successMessage: null,
          apply: () => {
            resetForm();
            appliedSignupKeyRef.current = "";
            onPaperTeamSaved();
          },
        };
      }

      if (createAnother) {
        return {
          shouldClose: false,
          successMessage: null,
          apply: () => {
            resetForm();
            if (selectedEvent?.bowlerQuantity) {
              initializeBowlers(selectedEvent.bowlerQuantity);
            } else {
              resetBowlers();
            }
          },
        };
      }

      return {
        shouldClose: true,
        successMessage: null,
        apply: () => {
          resetForm();
          resetBowlers();
          onClose();
        },
      };
    },
    [
      bowlers,
      doublesSinglesSetup,
      eventsForForm,
      initializeBowlers,
      onClose,
      onPaperTeamSaved,
      paperSignup,
      resetBowlers,
      resetForm,
      selectedEvent,
      setSelectedEvent,
      setSinglesFollowUp,
      singlesEvent,
    ]
  );

  const submitTeam = async (e, createAnother, { walkUpAnyway = false } = {}) => {
    e.preventDefault();

    const validationErrors = validateForm(
      selectedSquad,
      selectedEvent,
      teamName,
      bowlers
    );
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (!walkUpAnyway && selectedSquad && selectedEvent) {
      try {
        setIsSubmitting(true);
        const settings = await fetchTournamentSignupSettings(tournamentID);
        const cap = await fetchSquadWalkUpCap({
          squad: selectedSquad,
          event: selectedEvent,
          settings,
          events: eventsForForm,
          eventsBySquad,
        });
        if (cap.full) {
          setCapBlock({ createAnother, ...cap });
          setIsSubmitting(false);
          return;
        }
      } catch (capError) {
        console.error("Error checking squad occupancy:", capError);
        setIsSubmitting(false);
        notify.error("Could not check whether this squad is full.");
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const plan = planAfterTeamSaved({ createAnother });
      await handleSubmit(
        e,
        selectedEvent,
        teamName,
        bowlers,
        currentOrganization,
        user,
        selectedSquad?.id,
        tournamentID,
        setTeamsWithLane,
        setIsSubmitting,
        plan.apply,
        plan.shouldClose,
        plan.successMessage
      );
      setCapBlock(null);
    } catch (error) {
      console.error("Error saving team:", error);
    }
  };

  const handleSave = (e) => submitTeam(e, false);

  const handleSaveAndCreateAnother = (e) => submitTeam(e, true);

  const paperMode = Boolean(paperSignup);

  // Handle cancel with confirmation
  const handleCancelWithConfirmation = () => {
    if (paperMode) {
      onClose();
      return;
    }
    if (hasChanges) {
      handleCancel();
    } else {
      onClose();
    }
  };

  const handleConfirmCancelAction = () => {
    handleConfirmCancel();
    setSinglesFollowUp(null);
    onClose();
  };

  const paperRemaining =
    paperCount > 0 ? Math.max(0, paperCount - paperIndex - 1) : 0;

  const handleDeleteSignup = async () => {
    if (!onPaperTeamDelete) return;
    setIsSubmitting(true);
    try {
      await onPaperTeamDelete();
    } catch (error) {
      console.error("Error deleting pending signup:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="card-board overflow-hidden">
          <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
            <h2 className="font-display font-bold text-xl text-brand-cream tracking-tight">
              Create Team
            </h2>
          </div>
          <div className="p-6">
            <FormFieldsSkeleton fields={6} />
          </div>
        </div>
      </div>
    );
  }

  if (dataError) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <AlertBox
          type="error"
          message={sanitizeErrorMessage(dataError, "load tournament data")}
        />
      </div>
    );
  }

  return (
    <div className={paperMode ? "w-full" : "max-w-6xl mx-auto"}>
      <div className="card-board overflow-hidden">
        {/* Header */}
        <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-display font-bold text-xl text-brand-cream tracking-tight">
                Create Team
              </h2>
              {paperMode && paperCount > 0 ? (
                <p className="text-sm text-brand-mist mt-1">
                  Signup {paperIndex + 1} of {paperCount}
                </p>
              ) : null}
            </div>
            <IconButton
              onClick={handleCancelWithConfirmation}
              icon={FaTimes}
              variant="ghost"
              size="sm"
              className="!text-brand-cream !bg-white/15 hover:!bg-white/25"
            />
          </div>
        </div>

        <form onSubmit={handleSave} className="p-6 space-y-6">
          {/* Context Header */}
          <ContextHeader
            squads={squads}
            events={squadEvents}
            selectedSquad={selectedSquad}
            selectedEvent={selectedEvent}
            onSquadChange={handleSquadChange}
            onEventChange={handleEventChange}
            errors={errors}
            locked={paperMode || singlesFollowUpActive}
          />

          {/* Team Details Section - Only show for team events */}
          {selectedEvent?.bowlerQuantity > 2 && (
            <TeamDetailsSection
              teamName={teamName}
              onTeamNameChange={handleTeamNameChange}
              onTeamNameBlur={handleTeamNameBlur}
              errors={errors}
              selectedEvent={selectedEvent}
            />
          )}

          {canEnterBowlers ? (
            <LaneAssignmentFields
              startingPair={startingPair}
              onStartingPairChange={setStartingPair}
            />
          ) : null}

          {/* Bowlers Section */}
          <div className="space-y-4">
            <h3 className="heading-display text-lg">Team Bowlers</h3>
            {singlesFollowUpActive ? (
              <p className="text-sm text-brand-muted">
                {createTeamSinglesFollowUpNote(
                  singlesFollowUp.remaining[0]?.name,
                  singlesFollowUp.remaining.length
                )}
              </p>
            ) : showSinglesToggle ? (
              <p className="text-sm text-brand-muted">
                {createTeamOptionalSinglesNote}
              </p>
            ) : null}

            {canEnterBowlers ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {bowlers.map((bowler, index) => (
                <BowlerCard
                  key={bowler.id}
                  bowler={bowler}
                  index={index}
                  onBowlerUpdate={handleBowlerUpdate}
                  onBowlerBlur={handleBowlerBlur}
                  sidePotEvents={sidePotEvents}
                  onSidePotToggle={handleBowlerSidePotToggle}
                  bracketProducts={squadBracketProducts}
                  onTicketChange={handleBowlerTicketChange}
                  showSinglesToggle={showSinglesToggle}
                  singlesEvent={singlesEvent}
                  onSinglesToggle={handleBowlerSinglesToggle}
                  onLookupToggle={handleLookupToggle}
                  onLookupSearch={handleLookupSearch}
                  onLookupSelect={handleLookupSelect}
                  onCatalogSelect={handleCatalogSelect}
                  onCatalogClear={handleCatalogClear}
                  catalogName={catalogAssignment?.catalog?.name || ""}
                  existingBowlers={existingBowlers}
                  errors={errors}
                  nameInputRef={index === 0 ? firstBowlerNameRef : null}
                  allEventConfigs={allEventConfigs}
                  selectedEvent={selectedEvent}
                  getAllEventStatus={getAllEventStatus}
                />
              ))}
            </div>
            ) : (
              <div className="card-inset px-4 py-6 text-sm text-brand-muted">
                Bowler fields are locked until you choose a squad and event.
              </div>
            )}
          </div>

          {bowlers.some(needsOrgBowlerPick) ? (
            <p className="text-sm text-brand-charcoal">
              Pick which bowler this is, or add them as a new bowler, before you
              save.
            </p>
          ) : null}

          {/* Action Bar */}
          <ActionBar
            onSave={handleSave}
            onSaveAndCreateAnother={handleSaveAndCreateAnother}
            onCancel={handleCancelWithConfirmation}
            onSkip={paperMode && onPaperTeamSkip ? onPaperTeamSkip : null}
            skipLabel={paperMode ? "Skip signup" : "Skip team"}
            onDelete={
              paperMode && onPaperTeamDelete ? handleDeleteSignup : null
            }
            deleteLabel={paperMode ? "Delete signup" : "Delete team"}
            confirmResetKey={
              paperMode ? paperSignup?.key || String(paperIndex) : "create"
            }
            isSaving={isSubmitting}
            canSave={canSave(selectedEvent, bowlers, teamName)}
            showSaveAndCreateAnother={!paperMode && !singlesFollowUpActive}
            saveLabel={
              singlesFollowUpActive && singlesFollowUp.remaining.length > 1
                ? "Save and next Singles"
                : paperMode && paperRemaining > 0
                ? "Save and next signup"
                : "Save Team"
            }
          />
        </form>
      </div>

      {capBlock ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card-board p-6 max-w-md w-full mx-4">
            <h3 className="heading-display text-lg mb-2">This squad is full</h3>
            <p className="text-sm text-brand-muted mb-6">
              {capBlock.waitlistCount > 0
                ? `Waitlist has ${capBlock.waitlistCount} ${
                    capBlock.waitlistCount === 1 ? "name" : "names"
                  }. Put the next waitlist signup on the squad, or create this walk-up team anyway.`
                : "Create this walk-up team anyway? Online signup will see this squad as over the max."}
            </p>
            <div className="flex flex-col gap-2">
              {capBlock.waitlistCount > 0 ? (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      setIsSubmitting(true);
                      await directorPromoteNextWaitlist({
                        squadId: selectedSquad?.id,
                      });
                      notify.success(
                        "The next waitlist signup is on the squad."
                      );
                      setCapBlock(null);
                    } catch (promoteError) {
                      notify.error(
                        promoteError.message ||
                          "Could not put the next waitlist signup on the squad."
                      );
                    } finally {
                      setIsSubmitting(false);
                    }
                  }}
                  disabled={isSubmitting}
                  className="px-3 py-2 bg-brand-gold text-brand-charcoal rounded-lg text-sm font-display font-bold"
                >
                  Put next waitlist on squad
                </button>
              ) : null}
              <button
                type="button"
                onClick={() =>
                  submitTeam({ preventDefault() {} }, capBlock.createAnother, {
                    walkUpAnyway: true,
                  })
                }
                disabled={isSubmitting}
                className="px-3 py-2 bg-brand-charcoal text-brand-cream rounded-lg text-sm font-display font-bold"
              >
                Walk-up anyway
              </button>
              <button
                type="button"
                onClick={() => setCapBlock(null)}
                disabled={isSubmitting}
                className="px-3 py-2 text-sm text-brand-muted"
              >
                Don&apos;t create
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Cancel Confirmation */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card-board p-6 max-w-md w-full mx-4">
            <h3 className="heading-display text-lg mb-2">
              Unsaved Changes
            </h3>
            <p className="text-sm text-brand-muted mb-6">
              You have unsaved changes. Are you sure you want to cancel?
            </p>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={handleConfirmCancelAction}
                className="px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-display font-bold hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                Yes, Cancel
              </button>
              <button
                type="button"
                onClick={handleCancelCancel}
                className="px-3 py-2 border border-brand-charcoal/20 text-brand-charcoal rounded-lg text-sm font-display font-bold hover:bg-brand-charcoal/5"
              >
                Keep Editing
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SinglePageTeamCreation;
